import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./ScreenTransitioner.module.css";
import { ScreenTransitionDirection } from "./ScreenTransitionDirection";

interface ScreenTransitionerState {
  activeIndex?: string;
  direction: ScreenTransitionDirection;
  duration?: number;
}

interface ScreenTransitionerProps extends BoxProps {
  screens: Record<string, React.ReactNode>;
  startIndex: string;
}

interface Custom {
  direction: ScreenTransitionDirection;
  duration: number;
}

export interface ScreenTransitionerHandle {
  setIndex: (
    activeIndex: string,
    options?: {
      direction?: ScreenTransitionDirection;
      noAnimate?: boolean;
    }
  ) => void;
}

// Dynamic slide variants that respond to direction
const slideVariants = {
  enter: (custom: Custom) => {
    return {
      x: custom.direction === ScreenTransitionDirection.Left ? "-100%" : "100%",
      opacity: 0,
    };
  },
  center: (custom: Custom) => ({
    x: 0,
    opacity: 1,
    transition:
      custom.duration === 0
        ? {
            duration: 0,
          }
        : {
            x: {
              type: "tween" as const,
              duration: custom.duration,
              ease: "easeOut",
            },
            opacity: {
              duration: custom.duration,
            },
          },
  }),
  exit: (custom: Custom) => {
    return {
      x: custom.direction === ScreenTransitionDirection.Left ? "100%" : "-100%",
      opacity: 0,
      transition:
        custom.duration === 0
          ? {
              duration: 0,
            }
          : {
              x: {
                type: "tween" as const,
                duration: custom.duration,
                ease: "easeIn",
              },
              opacity: {
                duration: custom.duration,
              },
            },
    };
  },
};

export const ScreenTransitioner = forwardRef<
  ScreenTransitionerHandle,
  ScreenTransitionerProps
>(({ screens, startIndex, ...props }, ref) => {
  const [{ direction, activeIndex, duration }, setState] =
    useState<ScreenTransitionerState>({
      direction: ScreenTransitionDirection.Right,
      activeIndex: startIndex,
    });

  useImperativeHandle(ref, () => ({
    setIndex: async (activeIndex, options) => {
      setState((prev) => ({
        ...prev,
        activeIndex,
        duration: options?.noAnimate ? 0 : 0.2,
        direction:
          options?.direction ?? ScreenTransitionDirection.Right,
      }));
    },
  }));

  const currentIndex = activeIndex || startIndex;

  return (
    <Box
      position="relative"
      w="100%"
      display="flex"
      flexDirection="column"
      mx="auto"
      overflow="hidden"
      {...props}
    >
      <AnimatePresence
        initial={false}
        custom={{
          direction,
          duration: duration === undefined ? 0.2 : duration,
        }}
        mode="popLayout"
      >
        <motion.div
          key={activeIndex}
          className={styles.slideWrapper}
          custom={{
            direction,
            duration: duration === undefined ? 0.2 : duration,
          }}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {screens[currentIndex]}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
});
